/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/**
 * Copy of enum with unique keys for the chart types. From ./ChartItems.tsx.
 *
 * Needed because create-react-app does not add exports from tsx file until runtime.
 * TODO: use enum in ChartItems when this issue is fixed:
 * https://github.com/wmonk/create-react-app-typescript/issues/356
 */
import { capitalize, isNil } from 'lodash';
import { MIN_AZIMUTH, MAX_AZIMUTH, MIN_ELEVATION, MAX_ELEVATION } from '../../../../shared/entities/terminal';

const DEFAULT_MIN_DEGREE = -180;
const DEFAULT_MAX_DEGREE = 360;
export const SPEED_AXIS_ID = 'speed-axis-id';
export const PACKET_LOSS_ID = 'ping-latency-id';

export enum ChartType {
  line = 'line',
  area = 'area',
  xrange = 'xrange',
  scatter = 'scatter',
  column = 'column',
  spline = 'spline',
}

export enum ChartItemKeys {
  timeline = 'timeline',
  speedAndAltitude = 'speedAndAltitude',
  flDataRate = 'flDataRate',
  rlDataRate = 'rlDataRate',
  packetLossPercentage = 'packetLossPercentage',
  pingAvgLatency = 'pingAvgLatency',
  fLProtocolUsage = 'fLProtocolUsage',
  fLProtocolUsageOtherTerminal = 'fLProtocolUsageOtherTerminal',
  rLProtocolUsage = 'rLProtocolUsage',
  rLProtocolUsageOtherTerminal = 'rLProtocolUsageOtherTerminal',
  cumDataUsage = 'cumDataUsage',
  antennaPointingAngles = 'antennaPointingAngles',
  antennaPointingAnglesOtherTerminal = 'antennaPointingAnglesOtherTerminal',
  totalDataVolume = 'totalDataVolume',
  snr = 'snr',
  power = 'power',
}

export interface ChartSeriesMetadata {
  key: string;
  label: string | ((dynamicKey: string) => string);
  units: string;
  dynamicData?: boolean; // the series key will be used to get dynamic data in the form of an object. Note: dynamic series names are not supported
  hideTerminalTypeOnLabel?: boolean;
  min?: number;
  max?: number;
  className?: string | string[]; // array of class names will be used sequentially for dynamic data
  alternateClassName?: string;
  yAxisKey?: string;
  type?: ChartType;

  // TODO: add series sort functionality
}

interface ChartSeriesMetadataList {
  [key: string]: ChartSeriesMetadata[];
}

const antennaPointingAnglesContent = [
  {
    key: 'azimuthDeg',
    label: 'Azimuth',
    hideTerminalTypeOnLabel: true,
    units: 'degrees',
    min: MIN_AZIMUTH,
    max: MAX_AZIMUTH,
    className: 'ftv__charts__series--purple',
  },
  {
    key: 'elevationDeg',
    label: 'Elevation',
    hideTerminalTypeOnLabel: true,
    units: 'degrees',
    min: MIN_ELEVATION,
    max: MAX_ELEVATION,
    className: 'ftv__charts__series--skyBlue',
  },
  {
    key: 'skewDeg',
    label: 'Skew',
    hideTerminalTypeOnLabel: true,
    units: 'degrees',
    min: DEFAULT_MIN_DEGREE,
    max: DEFAULT_MAX_DEGREE,
    className: 'ftv__charts__series--magenta',
  },
  {
    key: 'polarizationDeg',
    label: 'Polarization',
    hideTerminalTypeOnLabel: true,
    units: 'degrees',
    min: DEFAULT_MIN_DEGREE,
    max: DEFAULT_MAX_DEGREE,
    className: 'ftv__charts__series--canaryYellow',
  },
];

/**
 * Capitalizes a series name. If the name includes a / then the preceding string
 * will be capitalized as well.
 * @param seriesName the series name to be capitalized.
 */
export const capitalizeSeriesName = (seriesName: string) => {
  if (isNil(seriesName)) {
    return '';
  }

  let splitSeriesName = [seriesName];
  let seperator = '';

  return splitSeriesName.map(val => val.charAt(0).toUpperCase() + val.slice(1)).join(seperator);
};

const protocolUsageContent = (termHistoryKey: 'rlRates' | 'flRates') => [
  {
    key: termHistoryKey,
    dynamicData: true,
    label: (seriesName: string) => capitalize(seriesName), // capitalize the dynamic series name
    hideTerminalTypeOnLabel: true,
    units: 'bps',
    className: [
      'ftv__charts__series--freshCutGrassGreen',
      'ftv__charts__series--standardOrange',
      'ftv__charts__series--creamyWhite',
      'ftv__charts__series--vibrantPink',
      'ftv__charts__series--cozyTurquoise',
      'ftv__charts__series--violetDelight',
      'ftv__charts__series--slimeGreen',
      'ftv__charts__series--emoPink',
      'ftv__charts__series--darkForestGreen',
      'ftv__charts__series--earlGrey',
    ],
  },
];

export const chartSeriesContent: ChartSeriesMetadataList = {
  [ChartItemKeys.timeline]: [
    { key: 'networkOrigin', label: 'Timeline', units: '', className: 'ftv__charts__timeline' },
  ],
  [ChartItemKeys.speedAndAltitude]: [
    {
      key: 'velocityKnots',
      label: 'Speed',
      hideTerminalTypeOnLabel: true,
      units: 'knots',
      className: 'ftv__charts__series--burntOrange',
      yAxisKey: SPEED_AXIS_ID,
    },
    {
      key: 'altitudeFeet',
      label: 'Altitude',
      hideTerminalTypeOnLabel: true,
      units: 'feet',
      className: 'ftv__charts__series--canaryYellow',
    },
  ],
  [ChartItemKeys.totalDataVolume]: [
    {
      key: 'gxTotalTransmitted',
      label: 'Transmitted',
      units: 'bps',
      className: 'ftv__charts__series--purple',
      alternateClassName: 'ftv__charts__series--turquoise',
      type: ChartType.line,
      min: 0,
    },
    {
      key: 'gxTotalReceived',
      label: 'Received',
      units: 'bps',
      className: 'ftv__charts__series--magenta .highcharts-point',
      type: ChartType.line,
      min: 0,
    },
  ],
  [ChartItemKeys.snr]: [
    {
      key: 'gxCurrentSnr',
      label: 'Current',
      units: 'dB',
      className: 'ftv__charts__series--cozyTurquoise',
      type: ChartType.line,
      min: 0,
    },
    {
      key: 'gxCalibratedSnr',
      label: 'Calibrated',
      units: 'dB',
      className: 'ftv__charts__series--vividRedOrange',
      type: ChartType.line,
      min: 0,
    },
  ],
  [ChartItemKeys.power]: [
    {
      key: 'gxPowerTransmitted',
      label: 'Transmit',
      units: 'dB',
      className: 'ftv__charts__series--purple',
      type: ChartType.line,
    },
    {
      key: 'gxPowerReceived',
      label: 'Receive',
      units: 'dB',
      className: 'ftv__charts__series--magenta',
      alternateClassName: 'ftv__charts__series--skyBlue',
      type: ChartType.line,
    },
    {
      key: 'gxPowerHeadroom',
      label: 'Headroom',
      units: 'dB',
      className: 'ftv__charts__series--beige--dashed',
      type: ChartType.line,
    },
    {
      key: 'gxPowerFootroom',
      label: 'Footroom',
      units: 'dB',
      className: 'ftv__charts__series--lightTurqoise--dashed',
      type: ChartType.line,
    },
  ],
  [ChartItemKeys.flDataRate]: [
    {
      key: 'flDataRateBps',
      label: 'FL Data Rate',
      units: 'bps',
      className: 'ftv__charts__series--purple',
      alternateClassName: 'ftv__charts__series--turquoise',
      type: ChartType.line,
      min: 0,
    },
    {
      key: 'flSlaUpperBoundBps',
      label: 'SLA',
      units: 'bps',
      className: 'ftv__charts__series--lightPurple--dashed',
      alternateClassName: 'ftv__charts__series--lightTurqoise--dashed',
      type: ChartType.line,
      min: 0,
    },
  ],
  [ChartItemKeys.rlDataRate]: [
    {
      key: 'rlDataRateBps',
      label: 'RL Data Rate',
      units: 'bps',
      className: 'ftv__charts__series--magenta',
      alternateClassName: 'ftv__charts__series--skyBlue',
      type: ChartType.line,
      min: 0,
    },
    {
      key: 'rlSlaUpperBoundBps',
      label: 'SLA',
      units: 'bps',
      className: 'ftv__charts__series--lightPink--dashed',
      alternateClassName: 'ftv__charts__series--lightSkyBlue--dashed',
      type: ChartType.line,
      min: 0,
    },
  ],
  [ChartItemKeys.packetLossPercentage]: [
    {
      key: 'packetLossPercentage',
      label: 'Packet Loss',
      units: '%',
      min: 0,
      max: 101,
      className: 'ftv__charts__series--purple',
      alternateClassName: 'ftv__charts__series--turquoise',
      yAxisKey: PACKET_LOSS_ID,
      type: ChartType.column,
    },
    {
      key: 'pingAvgLatency',
      label: 'Ping Latency',
      units: 'ms',
      min: 0,
      max: 5000,
      className: 'ftv__charts__series--magenta',
      alternateClassName: 'ftv__charts__series--skyBlue',
      type: ChartType.line,
    },
  ],
  [ChartItemKeys.fLProtocolUsage]: protocolUsageContent('flRates'),
  [ChartItemKeys.rLProtocolUsage]: protocolUsageContent('rlRates'),
  [ChartItemKeys.fLProtocolUsageOtherTerminal]: protocolUsageContent('flRates'),
  [ChartItemKeys.rLProtocolUsageOtherTerminal]: protocolUsageContent('rlRates'),
  [ChartItemKeys.cumDataUsage]: [
    {
      key: 'flDataUseBytes',
      label: 'FL Data Usage',
      units: 'Bytes',
      className: 'ftv__charts__series--purple',
      alternateClassName: 'ftv__charts__series--turquoise',
    },
    {
      key: 'rlDataUseBytes',
      label: 'RL Data Usage',
      units: 'Bytes',
      className: 'ftv__charts__series--magenta',
      alternateClassName: 'ftv__charts__series--skyBlue',
    },
  ],
  [ChartItemKeys.antennaPointingAngles]: antennaPointingAnglesContent,
  [ChartItemKeys.antennaPointingAnglesOtherTerminal]: antennaPointingAnglesContent,
};

// A space vehicle shows the units of its details panel. The keys come from toSpaceVehicleChartHistory.
export const spaceVehicleSpeedAndAltitudeSeriesContent: ChartSeriesMetadata[] = [
  {
    key: 'velocityMph',
    label: 'Speed',
    hideTerminalTypeOnLabel: true,
    units: 'mph',
    className: 'ftv__charts__series--burntOrange',
    yAxisKey: SPEED_AXIS_ID,
  },
  {
    key: 'altitudeMiles',
    label: 'Altitude',
    hideTerminalTypeOnLabel: true,
    units: 'mi',
    className: 'ftv__charts__series--canaryYellow',
  },
];
